/**
 * Leads Types
 * Tipos e interfaces para o módulo de Leads
 */

// ============================================================
// ENUMS
// ============================================================

export enum LeadStage {
  CONTATO_INICIAL = 'CONTATO_INICIAL',
  QUALIFICACAO = 'QUALIFICACAO',
  DISCUSSAO = 'DISCUSSAO',
  DECISAO = 'DECISAO',
  CONVERTIDO = 'CONVERTIDO',
  PERDIDO = 'PERDIDO'
}

export enum LeadSource {
  WEBSITE = 'WEBSITE',
  REFERRAL = 'REFERRAL',
  SOCIAL_MEDIA = 'SOCIAL_MEDIA',
  COLD_CALL = 'COLD_CALL',
  EVENT = 'EVENT',
  PARTNER = 'PARTNER',
  PAID_ADS = 'PAID_ADS',
  ORGANIC_SEARCH = 'ORGANIC_SEARCH',
  EMAIL_CAMPAIGN = 'EMAIL_CAMPAIGN',
  DIRECT = 'DIRECT',
  OTHER = 'OTHER'
}

export enum LeadPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high'
}

export enum LostReason {
  PRICE = 'PRICE',
  NO_RESPONSE = 'NO_RESPONSE',
  CHOSE_COMPETITOR = 'CHOSE_COMPETITOR',
  NOT_QUALIFIED = 'NOT_QUALIFIED',
  TIMING = 'TIMING',
  NO_BUDGET = 'NO_BUDGET',
  NO_AUTHORITY = 'NO_AUTHORITY',
  OTHER = 'OTHER'
}

export enum InteractionType {
  CALL = 'CALL',
  EMAIL = 'EMAIL',
  MEETING = 'MEETING',
  WHATSAPP = 'WHATSAPP',
  SMS = 'SMS',
  PROPOSAL_SENT = 'PROPOSAL_SENT',
  CONTRACT_SENT = 'CONTRACT_SENT',
  FOLLOW_UP = 'FOLLOW_UP',
  NOTE = 'NOTE',
  OTHER = 'OTHER'
}

// ============================================================
// MAIN INTERFACES
// ============================================================

export interface Lead {
  id: string;
  leadNumber: string;
  name: string;
  email: string | null;
  whatsapp: string | null;
  companyName: string | null;
  legalArea: string | null;

  // Status & Stage
  stage: LeadStage;
  source: LeadSource;
  priority: LeadPriority;

  // Financial
  estimatedValue: number | null;

  // Scoring & Qualification
  score: number;
  isHotLead: boolean;
  isQualified: boolean;

  // Assignment
  assignedTo: string | null;
  assignedToName: string | null;

  // Loss tracking
  lostReason: LostReason | null;
  lostNotes: string | null;
  lostAt: string | null;

  // Conversion tracking
  convertedAt: string | null;
  convertedClientId: string | null;
  convertedBy: string | null;

  // Follow-up
  nextFollowUpDate: string | null;
  lastInteractionAt: string | null;
  lastInteractionType: InteractionType | null;

  // Additional info
  notes: string | null;
  tags: string[];

  // Metadata
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  createdByName: string;

  // UTM params (marketing)
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmTerm: string | null;
  utmContent: string | null;
}

export interface LeadInteraction {
  id: string;
  leadId: string;
  type: InteractionType;
  notes: string;
  duration: number | null; // em minutos
  outcome: string | null;
  scheduleFollowUp: boolean;
  followUpDate: string | null;
  createdAt: string;
  createdBy: string;
  createdByName: string;
}

export interface LeadDocument {
  id: string;
  leadId: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  filePath: string;
  description: string | null;
  uploadedBy: string;
  uploadedByName: string;
  uploadedAt: string;
}

export interface LeadStageHistory {
  id: string;
  leadId: string;
  fromStage: LeadStage | null;
  toStage: LeadStage;
  notes: string | null;
  changedBy: string;
  changedByName: string;
  changedAt: string;
}

// ============================================================
// SCORE BREAKDOWN
// ============================================================

export interface ScoreFactors {
  budget: number; // 0-25
  urgency: number; // 0-25
  fit: number; // 0-30
  engagement: number; // 0-20
}

export interface ScoreInsight {
  factor: keyof ScoreFactors;
  currentPoints: number;
  maxPoints: number;
  suggestions: string[];
}

// ============================================================
// REQUEST/RESPONSE TYPES
// ============================================================

export interface CreateLeadRequest {
  name: string;
  email?: string;
  whatsapp?: string;
  companyName?: string;
  legalArea?: string;
  source: LeadSource;
  priority?: LeadPriority;
  estimatedValue?: number;
  notes?: string;
  tags?: string[];
  assignedTo?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

export interface UpdateLeadRequest {
  name?: string;
  email?: string;
  whatsapp?: string;
  companyName?: string;
  legalArea?: string;
  source?: LeadSource;
  priority?: LeadPriority;
  estimatedValue?: number;
  notes?: string;
  tags?: string[];
  assignedTo?: string;
}

export interface UpdateStageRequest {
  stage: LeadStage;
  notes?: string;
}

export interface MarkAsLostRequest {
  reason: LostReason;
  notes?: string;
}

export interface CreateInteractionRequest {
  type: InteractionType;
  notes: string;
  duration?: number;
  outcome?: string;
  scheduleFollowUp?: boolean;
  followUpDate?: string;
}

export interface ConvertToClientRequest {
  clientType: 'PERSON' | 'COMPANY';
  additionalNotes?: string;
}

// ============================================================
// FILTERS & PAGINATION
// ============================================================

export interface LeadFilters {
  search?: string;
  stage?: LeadStage;
  source?: LeadSource;
  priority?: LeadPriority;
  assignedTo?: string;
  unassigned?: boolean;
  isHotLead?: boolean;
  isQualified?: boolean;
  minScore?: number;
  maxScore?: number;
  hasOverdueFollowUp?: boolean;
  hasFollowUpToday?: boolean;
  legalArea?: string;
  minEstimatedValue?: number;
  maxEstimatedValue?: number;
  createdFrom?: string;
  createdTo?: string;
  tags?: string[];
}

export interface LeadPaginationOptions {
  limit?: number;
  offset?: number;
  sortBy?: 'createdAt' | 'updatedAt' | 'score' | 'estimatedValue' | 'nextFollowUpDate';
  sortOrder?: 'ASC' | 'DESC';
}

export interface LeadListResponse {
  leads: Lead[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

// ============================================================
// STATISTICS
// ============================================================

export interface LeadStats {
  totalLeads: number;
  hotLeads: number;
  qualified: number;
  overdueFollowUps: number;
  todayFollowUps: number;

  // Por estágio
  byStage: {
    [key in LeadStage]: number;
  };

  // Por fonte
  bySource: {
    [key in LeadSource]: number;
  };

  // Por prioridade
  byPriority: {
    [key in LeadPriority]: number;
  };

  // Métricas
  averageScore: number;
  totalEstimatedValue: number;
  conversionRate: number; // %
}

// ============================================================
// CONVERSION RESULT
// ============================================================

export interface ConversionResult {
  success: boolean;
  clientId: string;
  lead: Lead;
  message: string;
}

// ============================================================
// UTILITY TYPES
// ============================================================

export type LeadFormData = CreateLeadRequest;

export interface LeadKanbanColumn {
  stage: LeadStage;
  title: string;
  color: string;
  leads: Lead[];
  count: number;
}

// ============================================================
// CONSTANTS
// ============================================================

export const LEAD_STAGE_LABELS: Record<LeadStage, string> = {
  [LeadStage.CONTATO_INICIAL]: 'Contato Inicial',
  [LeadStage.QUALIFICACAO]: 'Qualificação',
  [LeadStage.DISCUSSAO]: 'Discussão',
  [LeadStage.DECISAO]: 'Decisão',
  [LeadStage.CONVERTIDO]: 'Convertido',
  [LeadStage.PERDIDO]: 'Perdido'
};

export const LEAD_SOURCE_LABELS: Record<LeadSource, string> = {
  [LeadSource.WEBSITE]: 'Website',
  [LeadSource.REFERRAL]: 'Indicação',
  [LeadSource.SOCIAL_MEDIA]: 'Redes Sociais',
  [LeadSource.COLD_CALL]: 'Ligação Fria',
  [LeadSource.EVENT]: 'Evento',
  [LeadSource.PARTNER]: 'Parceiro',
  [LeadSource.PAID_ADS]: 'Anúncios Pagos',
  [LeadSource.ORGANIC_SEARCH]: 'Busca Orgânica',
  [LeadSource.EMAIL_CAMPAIGN]: 'Campanha de Email',
  [LeadSource.DIRECT]: 'Contato Direto',
  [LeadSource.OTHER]: 'Outro'
};

export const LEAD_PRIORITY_LABELS: Record<LeadPriority, string> = {
  [LeadPriority.LOW]: 'Baixa',
  [LeadPriority.MEDIUM]: 'Média',
  [LeadPriority.HIGH]: 'Alta'
};

export const LOST_REASON_LABELS: Record<LostReason, string> = {
  [LostReason.PRICE]: 'Preço muito alto',
  [LostReason.NO_RESPONSE]: 'Sem resposta',
  [LostReason.CHOSE_COMPETITOR]: 'Escolheu concorrente',
  [LostReason.NOT_QUALIFIED]: 'Não qualificado',
  [LostReason.TIMING]: 'Momento inadequado',
  [LostReason.NO_BUDGET]: 'Sem orçamento',
  [LostReason.NO_AUTHORITY]: 'Sem autoridade para decidir',
  [LostReason.OTHER]: 'Outro motivo'
};

export const INTERACTION_TYPE_LABELS: Record<InteractionType, string> = {
  [InteractionType.CALL]: 'Ligação',
  [InteractionType.EMAIL]: 'Email',
  [InteractionType.MEETING]: 'Reunião',
  [InteractionType.WHATSAPP]: 'WhatsApp',
  [InteractionType.SMS]: 'SMS',
  [InteractionType.PROPOSAL_SENT]: 'Proposta Enviada',
  [InteractionType.CONTRACT_SENT]: 'Contrato Enviado',
  [InteractionType.FOLLOW_UP]: 'Follow-up',
  [InteractionType.NOTE]: 'Nota',
  [InteractionType.OTHER]: 'Outro'
};
